import React, { useState, useEffect } from "react";
import { Copy, Download, Loader2, Plus, Save, Upload } from "lucide-react";
import { clientsApi, authFetch } from "../../utils/api";
import SearchBar from "../common/SearchBar";
import FormModal from "../common/FormModal";
import ActionButtons, {
  PrimaryButton,
  SecondaryButton,
} from "../common/ActionButtons";
import ClientDetail from "./ClientDetail";
import ClientCloneModal from "./ClientCloneModal";
import ClientRestoreModal from "./ClientRestoreModal";

const ClientManagement = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [cloningClient, setCloningClient] = useState(null);
  const [restoringClient, setRestoringClient] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    is_active: true,
    settings: {},
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const data = await clientsApi.getAll();
      setClients(data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingClient) {
        await clientsApi.update(editingClient.id, formData);
      } else {
        await clientsApi.create(formData);
      }
      fetchClients();
      resetForm();
    } catch (error) {
      console.error("Error saving client:", error);
    }
    setLoading(false);
  };

  // Config-only export: what an admin authored to set the tenant up, not what
  // the product produced by running. Stage 1 of backup / clone / restore —
  // all three read hyveai/docker/auth/client_manifest.py, so a table
  // classified once is classified for all of them.
  const [exportingId, setExportingId] = useState(null);

  const handleExport = async (client) => {
    setExportingId(client.id);
    try {
      const data = await authFetch(`/admin/clients/${client.id}/export`);
      if (data?.error) {
        alert(`Export failed: ${data.error}`);
        return;
      }
      const rows = Object.values(data.row_counts || {}).reduce(
        (a, b) => a + b,
        0,
      );
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `${(client.client_name || client.name || "client")
        .replace(/[^a-z0-9]+/gi, "-")
        .toLowerCase()}-config-${stamp}.json`;
      a.click();
      URL.revokeObjectURL(url);
      alert(
        `Exported ${rows} config rows across ${data.facility_ids?.length ?? 0} ` +
          `facilities.

Config only — knowledge base, conversations, field data ` +
          `and credentials are deliberately excluded. The file lists what was ` +
          `left out and why.`,
      );
    } catch {
      alert("Network error — nothing was exported.");
    }
    setExportingId(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this client?")) return;
    setLoading(true);
    try {
      await clientsApi.delete(id);
      fetchClients();
    } catch (error) {
      console.error("Error deleting client:", error);
    }
    setLoading(false);
  };

  const handleEdit = (client) => {
    setSelectedClient(client);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      slug: "",
      description: "",
      is_active: true,
      settings: {},
    });
    setEditingClient(null);
    setShowForm(false);
  };

  if (selectedClient) {
    return (
      <ClientDetail
        client={selectedClient}
        onBack={() => setSelectedClient(null)}
        onSaved={fetchClients}
      />
    );
  }

  const filteredClients = clients.filter(
    (client) =>
      client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.slug?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          onRefresh={fetchClients}
          placeholder="Search clients..."
        />
        <PrimaryButton onClick={() => setShowForm(!showForm)} icon={Plus}>
          New Client
        </PrimaryButton>
      </div>

      {/* Form */}
      <FormModal
        title={editingClient ? "Edit Client" : "New Client"}
        isOpen={showForm}
        onClose={resetForm}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Name *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Slug *
              </label>
              <input
                type="text"
                required
                value={formData.slug}
                onChange={(e) =>
                  setFormData({ ...formData, slug: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex items-center">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) =>
                  setFormData({ ...formData, is_active: e.target.checked })
                }
                className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm font-medium text-slate-700">
                Active
              </span>
            </label>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <SecondaryButton onClick={resetForm}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" disabled={loading} icon={Save}>
              {loading ? "Saving..." : "Save Client"}
            </PrimaryButton>
          </div>
        </form>
      </FormModal>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Slug
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {loading ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-6 py-4 text-center text-slate-500"
                >
                  Loading...
                </td>
              </tr>
            ) : filteredClients.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-6 py-4 text-center text-slate-500"
                >
                  No clients found
                </td>
              </tr>
            ) : (
              filteredClients.map((client) => (
                <tr key={client.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                    {client.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                    {client.slug}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {client.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        client.is_active
                          ? "bg-green-100 text-green-800"
                          : "bg-slate-100 text-slate-800"
                      }`}
                    >
                      {client.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setRestoringClient(client)}
                        title="Restore this client's configuration from an export file. Plans before it writes."
                        className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded"
                      >
                        <Upload className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setCloningClient(client)}
                        title="Clone this client and its facilities. Config only; previews before writing."
                        className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleExport(client)}
                        disabled={exportingId === client.id}
                        title="Download this client's configuration as JSON. Read-only; no credentials or customer data."
                        className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded disabled:opacity-50"
                      >
                        {exportingId === client.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                      </button>
                      <ActionButtons
                        onEdit={() => handleEdit(client)}
                        onDelete={() => handleDelete(client.id)}
                      />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {restoringClient && (
        <ClientRestoreModal
          client={restoringClient}
          onClose={() => setRestoringClient(null)}
          onRestored={(r) => {
            fetchClients();
            alert(
              `Restored: ${r.added} added, ${r.changed} changed, ${r.deleted} deleted.`,
            );
          }}
        />
      )}
      {cloningClient && (
        <ClientCloneModal
          client={cloningClient}
          onClose={() => setCloningClient(null)}
          onCloned={() => {
            fetchClients();
            alert(
              "Clone created. The new client's knowledge base is EMPTY — " +
                "each facility has a fresh Pinecone namespace and no vectors " +
                "were copied.",
            );
          }}
        />
      )}
    </div>
  );
};

export default ClientManagement;
